import { createAsyncThunk, createSlice, } from '@reduxjs/toolkit'
import { AppState } from '@app/store/state/app.state';
import {
  AppConfigSection,
  ConfigEntity,
  DefaultConfigEntity,
  getAppConfig,
  getConfig,
  postUpdateConfig,
  setAdvanceHotDesking,
} from '@shared/services/ConfigService';
import { updateEditCompanyConfig } from '@features/settings/pages/edit-company-page/editCompany.duck';
import { loadCompanyStructure } from "./companyStructure.duck";
import { setOffices } from '@features/desks/store/advanceHotDeskingSetup.duck';
import { setTags } from "./userTags.duck";
import { setWhereaboutsOptions } from '@features/whereabouts/store/whereaboutsOptions.duck';
import { setTeamStructureForMovements } from '@features/whereabouts/store/companyMovements.duck';
import { TeamWithCount } from '@shared/models/team.models';
import { setAccountSummary } from '@features/settings/store/payments.duck';
import { setMeetingRooms } from '@features/settings/pages/meeting-rooms/meeting-rooms-settings.duck';
import { setDepartments } from '@features/whereabouts/pages/departments/data/departments.duck';

export interface ConfigState {
  configEntity: ConfigEntity;
  loading: boolean;
  globalConfigLoaded: boolean;
  teams: TeamWithCount[];
}

export const initialConfigState: ConfigState = {
  configEntity: DefaultConfigEntity,
  loading: false,
  globalConfigLoaded: false,
  teams: [],
}

const isPartialLoad = (sections?: AppConfigSection[]) => !!sections?.length;

// loadConfig() fetches everything (app start-up). After an edit, pass only the sections that changed,
// e.g. loadConfig(['teams']), so the browser doesn't download every office, option and list again.
export const loadConfig: any = createAsyncThunk('config/loadAppConfig',
  async (sections: AppConfigSection[] | undefined, thunkAPI) => {
    const appConfig = await getAppConfig(sections);
    const wants = (section: AppConfigSection) => !isPartialLoad(sections) || sections!.includes(section);
    if (wants('offices')) thunkAPI.dispatch(setOffices(appConfig.offices));
    if (wants('workGroups')) thunkAPI.dispatch(setTags(appConfig.workGroups));
    if (wants('teams')) {
      thunkAPI.dispatch(setTeamStructureForMovements(appConfig.teams));
      thunkAPI.dispatch(setTeams(appConfig.teams));
    }
    if (wants('whereaboutsOptions')) thunkAPI.dispatch(setWhereaboutsOptions(appConfig.whereaboutsOptions));
    if (wants('accountInfo')) thunkAPI.dispatch(setAccountSummary(appConfig.accountInfo));
    if (wants('meetingRooms')) thunkAPI.dispatch(setMeetingRooms(appConfig?.meetingRooms ?? []));
    if (wants('departments')) thunkAPI.dispatch(setDepartments(appConfig?.departments ?? []));
    if (wants('config') && appConfig.config) {
      const config = appConfig.config;
      thunkAPI.dispatch(updateEditCompanyConfig(config));
      thunkAPI.dispatch(setPlatformConfig(new ConfigEntity(
        config.holidayAllowance,
        config.numberOfDesksAvailable,
        config.companyHolidayAllowance,
        config.holidayYearRecurringMonth,
        config.holidayYearRecurringDay,
        config.carParkingEnabled,
        config.repeatWeeklySchedule,
        config.repeatScheduleWeeksLimit,
        config.restrictBookingWindowForTeamAdmins,
        config.visitorBookingEnabled,
        undefined, undefined,
        config.autoCarryOverEnabled,
        config.autoCarryOverDaysLimit,
        config.autoCarryOverExpiryMonth,
        config.autoCarryOverExpiryDay,
        config.hidePastWhereaboutsEnabled,
        config.hidePastWhereaboutsWeeksLimit,
        config.checkinConfirmBookingsEnabled,
        config.checkinConfirmedVisibleToEveryone,
        config.checkinUpdateWhereaboutsEnabled,
      )));
    }
    await new Promise((resolve: any) => setTimeout(() => resolve(), 100));
  }
)

const fetchConfig: any = createAsyncThunk(
  'config/fetchConfig',
  async (blank, thunkAPI) => {
    if (!(thunkAPI.getState() as AppState).config.loading) {
      await thunkAPI.dispatch(setLoading(true));
      try {
        const platformConfig = await getConfig();
        await thunkAPI.dispatch(setPlatformConfig(platformConfig));
        await thunkAPI.dispatch(updateEditCompanyConfig(platformConfig));
      } finally {
        await thunkAPI.dispatch(setLoading(false));
      }
    }
  }
)

export const updateConfig: any = createAsyncThunk(
  'config/updateConfig',
  async (configEntity: ConfigEntity, thunkAPI) => {
    const state = thunkAPI.getState() as AppState;
    if (state.config.loading) {
      // Reject rather than resolve with undefined so callers don't report
      // success for a save that never ran.
      return thunkAPI.rejectWithValue('Config update already in progress');
    }
    await thunkAPI.dispatch(setLoading(true));
    try {
      const config = await postUpdateConfig(configEntity);
      await thunkAPI.dispatch(loadCompanyStructure());
      return config;
    } finally {
      // reset
      await thunkAPI.dispatch(setLoading(false));
    }
  }
)

export const updateAdvanceHotDeskingConfig: any = createAsyncThunk(
  'config/updateAdvanceHotDeskingConfig',
  async (value: boolean, thunkAPI) => {
    const state = thunkAPI.getState() as AppState;
    if (!state.config.loading) {
      await thunkAPI.dispatch(setLoading(true));
      try {
        return await setAdvanceHotDesking(value);
      } finally {
        // reset
        await thunkAPI.dispatch(setLoading(false));
      }
    }
  }
)

const configSlice = createSlice({
  name: 'config',
  initialState: initialConfigState,
  reducers: {
    setLoading: (state, action) => ({
      ...state,
      loading: action.payload,
    }),
    setTeams: (state, action) => ({ ...state, teams: action.payload, }),
    setPlatformConfig: (state, action) => ({...state, configEntity: action.payload}),
    reset: () => ({...initialConfigState}),
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateConfig.fulfilled, (state, action) => action.payload ? {...state, configEntity: action.payload} : state)
      // A partial reload after an edit leaves the loaded/loading flags alone, so pages don't blank out.
      .addCase(loadConfig.pending, (state, action) => isPartialLoad(action.meta.arg) ? state : ({...state, loading: true, globalConfigLoaded: false}))
      .addCase(loadConfig.rejected, (state, action) => isPartialLoad(action.meta.arg) ? state : ({...state, globalConfigLoaded: false, loading: false}))
      .addCase(loadConfig.fulfilled, (state, action) => isPartialLoad(action.meta.arg) ? state : ({...state, loading: false, globalConfigLoaded: true}));
  }
});

export default configSlice.reducer
export const { setPlatformConfig } = configSlice.actions;
const { setLoading, setTeams } = configSlice.actions;

// Selectors
export const selectIsConfigLoading = (state: AppState) => state.config.loading;
export const selectConfig = (state: AppState) => state.config.configEntity;
export const selectHasGlobalConfigLoaded = (state: AppState) => state.config.globalConfigLoaded;
export const selectAllTeams = (state: AppState) => state.config.teams;
export const selectIsCarParkingEnabled = (state: AppState) => !!state.config.configEntity.carParkingEnabled;
export const selectIsRepeatWeeklyScheduleEnabled = (state: AppState) => !!state.config.configEntity.repeatWeeklySchedule;
export const selectRepeatScheduleWeeksLimit = (state: AppState) => state.config.configEntity.repeatScheduleWeeksLimit;
const selectIsHidePastWhereaboutsEnabled = (state: AppState) => !!state.config.configEntity.hidePastWhereaboutsEnabled;
const selectHidePastWhereaboutsWeeksLimit = (state: AppState) => state.config.configEntity.hidePastWhereaboutsWeeksLimit ?? 0;
